/**
 * Sign Up page — account creation form.
 *
 * Collects username, email, password, and password confirmation.
 * Real-time validation:
 *   - Username: debounced availability check via GET /auth/check-username.
 *   - Email: format check (client-side) + debounced availability check
 *     via GET /auth/check-email (only fires after format passes).
 *   - Password: client-side complexity rules.
 *   - Password confirm: match check.
 * On successful POST /auth/signup the user sees a success message
 * instructing them to check their email.
 *
 * @module pages/signup
 */

import React, { useState,
                useEffect,
                useRef,
                useCallback }   from 'react';
import { useNavigate }          from 'react-router-dom';
import { Eye, EyeOff,
         Check, X }             from 'lucide-react';
import Layout                    from '../components/Layout';
import { useI18n }               from '../dataProviders/I18nProvider';
import { useAuth }               from '../hooks/useAuth';
import apiClient                 from '../procedural/apiClient';
import { isPasswordComplex }     from '../procedural/passwordValidation';
import config                    from '../config';
import * as constants            from '../constants';

/** Debounce delay (ms) for async availability checks. */
const AVAILABILITY_CHECK_DELAY = 400;

/**
 * Check whether a string looks like a valid email address.
 * Uses a deliberately simple regex that matches `local@domain.tld`.
 * This is a UI convenience check, not an authoritative validator —
 * the server will reject truly invalid addresses at insertion time.
 *
 * @param {string} value - The string to test.
 * @returns {boolean} True if the format is plausible.
 */
function isEmailFormat(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}


/**
 * Renders the Sign Up form with real-time field validation.
 *
 * @returns {React.ReactElement}
 */
export default function Signup() {
  const { t }                  = useI18n();
  const { isAuthenticated }    = useAuth();
  const navigate               = useNavigate();

  const [username, setUsername]                        = useState('');
  const [email, setEmail]                              = useState('');
  const [password, setPassword]                        = useState('');
  const [passwordConfirm, setPasswordConfirm]          = useState('');
  const [showPassword, setShowPassword]                = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm]  = useState(false);
  const [error, setError]                              = useState(null);
  const [success, setSuccess]                          = useState(null);
  const [submitting, setSubmitting]                    = useState(false);

  /* Username availability state */
  const [usernameAvailable, setUsernameAvailable]      = useState(null);
  const [checkingUsername, setCheckingUsername]          = useState(false);
  const usernameDebounceRef                            = useRef(null);

  /* Email availability state */
  const [emailAvailable, setEmailAvailable]            = useState(null);
  const [checkingEmail, setCheckingEmail]              = useState(false);
  const emailDebounceRef                               = useRef(null);

  /* ---- Guards --------------------------------------------------------- */

  useEffect(() => {
    if (isAuthenticated) {
      navigate(config.pagePaths.dashboard, { replace: true });
    }
  }, [isAuthenticated, navigate]);

  /* ---- Username availability check ------------------------------------ */

  const checkUsername = useCallback(async (value) => {
    if (!value) {
      setUsernameAvailable(null);
      return;
    }
    setCheckingUsername(true);
    try {
      const res = await apiClient.get('/auth/check-username', {
        params: { username: value },
      });
      setUsernameAvailable(res.data.available);
    } catch {
      setUsernameAvailable(null);
    } finally {
      setCheckingUsername(false);
    }
  }, []);

  /**
   * Handle username input changes. Updates state and schedules a
   * debounced availability check.
   *
   * @param {React.ChangeEvent<HTMLInputElement>} e
   */
  function handleUsernameChange(e) {
    const value = e.target.value;
    setUsername(value);
    setUsernameAvailable(null);

    if (usernameDebounceRef.current) clearTimeout(usernameDebounceRef.current);

    if (value) {
      usernameDebounceRef.current = setTimeout(() => checkUsername(value), AVAILABILITY_CHECK_DELAY);
    }
  }

  /* ---- Email availability check --------------------------------------- */

  const checkEmail = useCallback(async (value) => {
    if (!value || !isEmailFormat(value)) {
      setEmailAvailable(null);
      return;
    }
    setCheckingEmail(true);
    try {
      const res = await apiClient.get('/auth/check-email', {
        params: { email: value },
      });
      setEmailAvailable(res.data.available);
    } catch {
      setEmailAvailable(null);
    } finally {
      setCheckingEmail(false);
    }
  }, []);

  /**
   * Handle email input changes. Updates state, validates format,
   * and schedules a debounced availability check when the format is valid.
   *
   * @param {React.ChangeEvent<HTMLInputElement>} e
   */
  function handleEmailChange(e) {
    const value = e.target.value;
    setEmail(value);
    setEmailAvailable(null);

    if (emailDebounceRef.current) clearTimeout(emailDebounceRef.current);

    if (value && isEmailFormat(value)) {
      emailDebounceRef.current = setTimeout(() => checkEmail(value), AVAILABILITY_CHECK_DELAY);
    }
  }

  /* ---- Derived validation state --------------------------------------- */

  const emailFormatOk  = email.length > 0 && isEmailFormat(email);
  const passwordValid  = password.length > 0 && isPasswordComplex(password);
  const passwordsMatch = passwordConfirm.length > 0 && password === passwordConfirm;

  /**
   * Combined email validity: format must pass, then the availability
   * check determines the icon.  Returns true (valid), false (invalid),
   * or null (no icon yet).
   */
  const emailValid = (() => {
    if (email.length === 0)       return null;
    if (!emailFormatOk)           return false;
    if (emailAvailable === null)  return null;
    return emailAvailable;
  })();

  /* ---- Form submission ------------------------------------------------ */

  /**
   * Submit the signup form to POST /auth/signup.
   *
   * @param {React.FormEvent} e - Form submit event.
   */
  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);

    if (!username || !email || !password || !passwordConfirm) {
      setError(t('signup.error.missingFields'));
      return;
    }
    if (!isPasswordComplex(password)) {
      setError(t('signup.error.passwordComplexity'));
      return;
    }
    if (password !== passwordConfirm) {
      setError(t('signup.error.passwordMismatch'));
      return;
    }

    setSubmitting(true);
    try {
      await apiClient.post('/auth/signup', { username, email, password });
      setSuccess(t('signup.success'));
      setError(null);
    } catch (err) {
      const errorCode = err.response?.data?.error;
      if (errorCode === 'conflict') {
        setError(t('signup.error.conflict'));
      } else {
        setError(t('signup.error.generic'));
      }
    } finally {
      setSubmitting(false);
    }
  }

  /* ---- Render helpers ------------------------------------------------- */

  /**
   * Render a validity icon (check or X) for a field.
   *
   * @param {boolean|null} valid - True = valid, false = invalid, null = hidden.
   * @returns {React.ReactElement|null}
   */
  function renderValidityIcon(valid) {
    if (valid === null) return null;
    return (
      <span className={`auth-validity-icon ${valid ? 'auth-validity-icon--valid' : 'auth-validity-icon--invalid'}`}>
        {valid ? <Check size={16} /> : <X size={16} />}
      </span>
    );
  }

  /* ---- Render --------------------------------------------------------- */

  return (
    <Layout title={t('page.signup')}>
      <div className="auth-container">
        <div className="auth-card">
          {success ? (
            <div className="auth-form">
              <div className="auth-success">{success}</div>
            </div>
          ) : (
            <form className="auth-form" onSubmit={handleSubmit}>
              {error && <div className="auth-error">{error}</div>}

              {/* Username */}
              <div className="auth-field">
                <label className="auth-label" htmlFor="signup-username">
                  {t('signup.username.label')}
                </label>
                <div className="auth-input-with-icon">
                  <input
                    id="signup-username"
                    className="auth-input"
                    type="text"
                    autoComplete="username"
                    maxLength={constants.MAX_USERNAME_LENGTH}
                    value={username}
                    onChange={handleUsernameChange}
                    disabled={submitting}
                  />
                  {!checkingUsername && renderValidityIcon(usernameAvailable)}
                </div>
              </div>

              {/* Email */}
              <div className="auth-field">
                <label className="auth-label" htmlFor="signup-email">
                  {t('signup.email.label')}
                </label>
                <div className="auth-input-with-icon">
                  <input
                    id="signup-email"
                    className="auth-input"
                    type="email"
                    autoComplete="email"
                    maxLength={constants.MAX_EMAIL_LENGTH}
                    value={email}
                    onChange={handleEmailChange}
                    disabled={submitting}
                  />
                  {!checkingEmail && renderValidityIcon(emailValid)}
                </div>
              </div>

              {/* Password */}
              <div className="auth-field">
                <label className="auth-label" htmlFor="signup-password">
                  {t('signup.password.label')}
                </label>
                <div className="auth-input-wrapper">
                  <input
                    id="signup-password"
                    className="auth-input"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    maxLength={constants.MAX_PASSWORD_LENGTH}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={submitting}
                  />
                  <button
                    type="button"
                    className="auth-toggle-password"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                <span className="auth-hint">
                  {t('signup.passwordRequirements')}
                </span>
                {password.length > 0 && (
                  <span className={passwordValid ? 'auth-validity-icon--valid' : 'auth-validity-icon--invalid'}
                        style={{ fontSize: '12px' }}>
                    {passwordValid
                      ? t('signup.passwordValid')
                      : t('signup.passwordInvalid')}
                  </span>
                )}
              </div>

              {/* Password Confirm */}
              <div className="auth-field">
                <label className="auth-label" htmlFor="signup-password-confirm">
                  {t('signup.passwordConfirm.label')}
                </label>
                <div className="auth-input-wrapper">
                  <input
                    id="signup-password-confirm"
                    className="auth-input"
                    type={showPasswordConfirm ? 'text' : 'password'}
                    autoComplete="new-password"
                    maxLength={constants.MAX_PASSWORD_LENGTH}
                    value={passwordConfirm}
                    onChange={(e) => setPasswordConfirm(e.target.value)}
                    disabled={submitting}
                  />
                  <button
                    type="button"
                    className="auth-toggle-password"
                    onClick={() => setShowPasswordConfirm(!showPasswordConfirm)}
                    aria-label={showPasswordConfirm ? 'Hide password' : 'Show password'}
                  >
                    {showPasswordConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {passwordConfirm.length > 0 && (
                  <span className={passwordsMatch ? 'auth-validity-icon--valid' : 'auth-validity-icon--invalid'}
                        style={{ fontSize: '12px' }}>
                    {passwordsMatch
                      ? t('signup.passwordMatch')
                      : t('signup.passwordMismatch')}
                  </span>
                )}
              </div>

              <button
                type="submit"
                className="auth-submit"
                disabled={submitting}
              >
                {t('signup.submit')}
              </button>
            </form>
          )}
        </div>
      </div>
    </Layout>
  );
}
